import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';

// Unstaffed work: the queue Resource Management works from (Phase 2).
//
//   Tasks with nobody on them   plan tasks with no person assigned, soonest
//                               first, each with up to three people who have
//                               free time across its dates (committed work
//                               only), preferring the resource line's band and
//                               the task's team. Assign here and the task is
//                               theirs, through the same route the plan uses.
//   Sold, not yet planned       resource lines whose sold days are more than
//                               the plan has scheduled against them.
//
// Draft and Issued SOWs are tentative, as everywhere: shown, and marked.

const r1 = (n) => Math.round((Number(n) || 0) * 10) / 10;
const fmtD = (d) => (d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '');

function Tentative({ row }) {
  if (!row.tentative) return null;
  return (
    <span className="ml-1 text-[7.5px] font-semibold text-indigo-700 bg-indigo-50 px-1 rounded">
      {row.sow_status}{row.win_probability != null ? `, ${row.win_probability}% likely` : ', no win % set'}
    </span>
  );
}

function AssignCell({ task, people, onAssigned }) {
  const [personId, setPersonId] = useState(task.candidates[0]?.person_id || '');
  const [state, setState] = useState(null);     // null | 'saving' | { ok, text }

  const assign = async () => {
    if (!personId) return;
    setState('saving');
    try {
      await api.put(`sows/${task.sow_id}/project-tasks/${task.task_id}`, { owner_id: personId });
      const name = people.find(p => p.id === personId);
      setState({ ok: true, text: `Assigned to ${name ? `${name.first_name} ${name.last_name}` : 'them'}` });
      onAssigned?.();
    } catch (e) {
      setState({ ok: false, text: e.message || 'Could not assign' });
    }
  };

  if (state?.ok) return <span className="text-[8.5px] font-semibold text-emerald-700">{state.text}</span>;
  return (
    <div className="space-y-1">
      {task.candidates.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {task.candidates.map(c => (
            <button key={c.person_id} type="button" onClick={() => setPersonId(c.person_id)}
              className={`text-[8px] px-1.5 py-0.5 rounded border ${personId === c.person_id ? 'border-navy bg-navy/10 text-navy' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              title={`${c.free_days}d free across the task's dates${c.covers ? ', enough to cover it' : ', not enough to cover it alone'}`}>
              {c.name} · {r1(c.free_days)}d free
              {c.same_band && <span className="ml-1 text-emerald-700">band</span>}
              {c.same_team && <span className="ml-1 text-sky-700">team</span>}
            </button>
          ))}
        </div>
      )}
      {task.start_date && task.candidates.length === 0 && (
        <div className="text-[8px] text-amber-700">Nobody has free time across these dates.</div>
      )}
      <div className="flex items-center gap-1">
        <select className="form-select text-[8.5px] py-0 max-w-[160px]" value={personId} onChange={e => setPersonId(e.target.value)}>
          <option value="">Choose a person…</option>
          {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
        </select>
        <button type="button" disabled={!personId || state === 'saving'} onClick={assign}
          className="px-2 py-0.5 text-[8.5px] font-semibold rounded bg-navy text-white disabled:opacity-40">
          {state === 'saving' ? 'Assigning…' : 'Assign'}
        </button>
      </div>
      {state && !state.ok && state !== 'saving' && <div className="text-[8px] text-red-700">{state.text}</div>}
    </div>
  );
}

export default function UnstaffedWork({ includeTentative = true, onChanged, onCount }) {
  const { navigate } = useApp();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        api.get('resource-load/unstaffed', { tentative: includeTentative ? 1 : 0 }),
        api.get('people', { status: 'active' }).catch(() => []),
      ]);
      setData(d);
      setPeople((p || []).filter(x => !String(x.user_role || '').includes('client')));
      onCount?.(d.tasks.length + d.lines.length + (d.allowances || []).length);
      setError('');
    } catch (e) { setError(e.message || 'Could not load unstaffed work.'); }
  }, [includeTentative, onCount]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>;
  if (!data) return <div className="py-8 text-center text-[10px] text-gray-400">Finding unstaffed work…</div>;

  const afterAssign = () => { onChanged?.(); };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Committed work with nobody on it</div>
          <div className="text-sm font-extrabold text-amber-700">{r1(data.totals.task_days)}d</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Bid, at win probability</div>
          <div className="text-sm font-extrabold text-indigo-700">{r1(data.totals.tentative_task_days)}d</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Team allowances not yet on a task</div>
          <div className="text-sm font-extrabold text-amber-700">{r1(data.totals.allowance_days)}d</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Sold on live SOWs, not yet planned</div>
          <div className="text-sm font-extrabold text-gray-700">{r1(data.totals.unplanned_line_days)}d</div>
        </div>
      </div>

      {/* Tasks */}
      <div>
        <h3 className="text-[11px] font-extrabold text-navy mb-1">Tasks with nobody on them ({data.tasks.length})</h3>
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-[9px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-[8px] uppercase text-gray-500 font-bold">
                <th className="px-3 py-1.5 text-left">When</th>
                <th className="px-2 py-1.5 text-left">Task</th>
                <th className="px-2 py-1.5 text-left">Line · team</th>
                <th className="px-2 py-1.5 text-right">Work</th>
                <th className="px-3 py-1.5 text-left min-w-[260px]">Who could take it</th>
              </tr>
            </thead>
            <tbody>
              {data.tasks.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Every open plan task has somebody on it.</td></tr>
              )}
              {data.tasks.map(t => (
                <tr key={t.task_id} className="border-b border-gray-100 align-top">
                  <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">
                    {t.start_date ? <>{fmtD(t.start_date)}<div className="text-[8px] text-gray-400">to {fmtD(t.end_date)}</div></> : <span className="text-gray-400">Not scheduled</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <button type="button" onClick={() => navigate('sow-detail', t.sow_id)} className="font-mono text-[8px] text-navy hover:underline">{t.sow_reference}</button>
                    <Tentative row={t} />
                    <div className="text-gray-800 font-semibold">{t.task_name}</div>
                    <div className="text-[8px] text-gray-400">{t.client_name || ''}</div>
                  </td>
                  <td className="px-2 py-1.5 text-gray-600">
                    {t.band_code ? <span className="font-semibold">{t.band_code}</span> : <span className="text-gray-400">No line</span>}
                    {t.team_name && <div className="text-[8px] text-gray-400">{t.team_name}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{r1(t.days_of_work)}d</td>
                  <td className="px-3 py-1.5"><AssignCell task={t} people={people} onAssigned={afterAssign} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[8px] text-gray-400 mt-1">
          Suggestions are people with free time across the task's dates, counting committed work only. "band" means the same band as the task's resource line; "team" the task's team.
        </div>
      </div>

      {/* Team allowances on summary bars */}
      <div>
        <h3 className="text-[11px] font-extrabold text-navy mb-1">Team time set aside on summary bars, not yet on a task ({(data.allowances || []).length})</h3>
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-[9px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-[8px] uppercase text-gray-500 font-bold">
                <th className="px-3 py-1.5 text-left">When</th>
                <th className="px-2 py-1.5 text-left">Summary bar</th>
                <th className="px-2 py-1.5 text-left">Team</th>
                <th className="px-2 py-1.5 text-right">Allowance</th>
                <th className="px-2 py-1.5 text-right">On tasks</th>
                <th className="px-2 py-1.5 text-right">Not yet on a task</th>
              </tr>
            </thead>
            <tbody>
              {(data.allowances || []).length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No team allowances are waiting to be broken into tasks.</td></tr>
              )}
              {(data.allowances || []).map(a => (
                <tr key={`${a.task_id}-${a.team_id}`} className="border-b border-gray-100">
                  <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">
                    {a.start_date ? <>{fmtD(a.start_date)}<div className="text-[8px] text-gray-400">to {fmtD(a.end_date)}</div></> : <span className="text-gray-400">Not scheduled</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <button type="button" onClick={() => navigate('sow-detail', a.sow_id)} className="font-mono text-[8px] text-navy hover:underline">{a.sow_reference}</button>
                    <Tentative row={a} />
                    <div className="text-gray-800 font-semibold">{a.name.replace(/: .* allowance$/, '')}</div>
                    <div className="text-[8px] text-gray-400">{a.client_name || ''}{a.band_code ? ` · ${a.band_code} line` : ''}</div>
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">{a.team_name}</td>
                  <td className="px-2 py-1.5 text-right">{r1(a.allowance_days)}d</td>
                  <td className="px-2 py-1.5 text-right">{r1(a.allowance_used_days)}d</td>
                  <td className="px-2 py-1.5 text-right font-semibold text-amber-700">{r1(a.left_days)}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[8px] text-gray-400 mt-1">
          A team allowance is set on a summary bar in the plan. The team's work on the tasks inside the bar draws it down; break the rest into tasks to staff it.
        </div>
      </div>

      {/* Lines */}
      <div>
        <h3 className="text-[11px] font-extrabold text-navy mb-1">Sold, not yet planned ({data.lines.length})</h3>
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-[9px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-[8px] uppercase text-gray-500 font-bold">
                <th className="px-3 py-1.5 text-left">SOW</th>
                <th className="px-2 py-1.5 text-left">Line</th>
                <th className="px-2 py-1.5 text-right">Sold</th>
                <th className="px-2 py-1.5 text-right">Planned</th>
                <th className="px-2 py-1.5 text-right">Not yet planned</th>
                <th className="px-3 py-1.5 text-left">Likely start</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Every resource line is fully planned.</td></tr>
              )}
              {data.lines.map(l => (
                <tr key={l.line_id} className="border-b border-gray-100">
                  <td className="px-3 py-1.5">
                    <button type="button" onClick={() => navigate('sow-detail', l.sow_id)} className="font-mono text-[8px] text-navy hover:underline">{l.sow_reference}</button>
                    <Tentative row={l} />
                    <div className="text-[8px] text-gray-400">{l.client_name || ''}</div>
                  </td>
                  <td className="px-2 py-1.5"><span className="font-semibold">{l.band_code}</span> <span className="text-gray-500">{l.band_name}</span>{l.description && <div className="text-[8px] text-gray-400">{l.description}</div>}</td>
                  <td className="px-2 py-1.5 text-right">{r1(l.sold_days)}d</td>
                  <td className="px-2 py-1.5 text-right">{r1(l.planned_days)}d</td>
                  <td className="px-2 py-1.5 text-right font-semibold text-amber-700">{r1(l.unplanned_days)}d</td>
                  <td className="px-3 py-1.5 text-gray-600">{l.likely_start_date ? fmtD(l.likely_start_date) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[8px] text-gray-400 mt-1">
          Open a SOW's Resourcing tab to plan the rest of a line: link plan tasks to it, or create its tasks in the plan.
        </div>
      </div>
    </div>
  );
}
